import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import {
    WB_EventProductGroupRow,
    WB_EventRow,
    WB_EventSpendPrdGrpRow,
    WB_EventSpendRow,
    WB_MetaRow,
    WB_PEAEventOverrideRow,
    WB_PEAEventProductGroupOverrideRow,
    WB_PEAEventSpendOverrideRow,
    WB_PEAEventSpendProductGroupOverrideRow,
    WB_ProductGroupRow,
} from "../PA.Core/PAdbSchemaRows";

import {
    esWB_PEA_Event_Override,
    esWB_PEA_Event_Spend_ProductGroup_Override,
    esWB_PEA_Event_Spend_Override,
    esWB_PEA_Event_ProductGroup_Override,
    esWB_Event,
    esWB_Event_Spend,
    esWB_Event_Meta,
    esWB_Event_Spend_Prd,
    esWB_Event_Spend_PrdGrp,
    esWB_EvtSShpPrd,
    esWB_Event_Spend_Type,
    esWB_Event_Tag,
    esWB_Event_Spend_Type_Account,
    esWB_EventFR,
    esWB_Event_Circular,
    esWB_EvtShipPrd,
    esWB_Event_Spend_PrdGrp_Redistrib,
    esWB_Event_Relationship,
    esWB_Event_Spend_PrdGrp_Customer,
    esWB_Event_Customer,
    esWB_Event_Product_Group,
    esWB_Meta,
    esWB_Product_Group,
    esWB_Plan_Group,
    esWB_Plan_Product_Group,
} from "../PA.Core/PAdbSchema";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { ProductHierarchy } from "../PA.MD.Services/ProductHierarchy";
import * as events from "events";

export class PEAEventOverrideBO implements IBusinessObject {
    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Override: WB_PEAEventOverrideRow;
    appliedOverride: WB_PEAEventOverrideRow;

    planPPGs: any[];

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New Override" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_Override = null;
        this.appliedOverride = undefined;

        this.planPPGs = [];
    }

    //</editor-fold>

    get WB_Override(): WB_PEAEventOverrideRow {
        return this.drWB_Override;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Event Override");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Event Override.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            self.LoadOptions = options || {};
            (async () => {
                try {
                    let type = "Date";
                    if ("Type" in self.LoadOptions && self.LoadOptions.Type == "Product") {
                        type = "Product";
                    }
                    let eventRow = await self.loadEvent(self.LoadOptions.WB_EVT_ID, type);
                    let newOverrideRow: WB_PEAEventOverrideRow = <WB_PEAEventOverrideRow>(
                        await this.dbContext.createRecordWithKey(esWB_PEA_Event_Override)
                    );
                    newOverrideRow.setDefaults();
                    newOverrideRow.cif.STATUS = "New";
                    newOverrideRow.cif.WB_AUTHUSR_ID = PAEnvironment.getValue(
                        PAEnvironment.UserContext,
                    ).User.cif.WB_AUTHUSR_ID;
                    self.drWB_Override = newOverrideRow;
                    self.drWB_Override.WB_EventRow = eventRow;
                    self.drWB_Override.cif.WB_EVT_ID = self.LoadOptions.WB_EVT_ID;
                    self.drWB_Override.cif.OVRD_INSTORE_END_DT = eventRow.cif.INSTORE_END_DT;
                    self.drWB_Override.cif.OVRD_INSTORE_START_DT = eventRow.cif.INSTORE_START_DT;
                    self.drWB_Override.cif.OVRD_SHIPMENT_END_DT = eventRow.cif.SHIPMENT_END_DT;
                    self.drWB_Override.cif.OVRD_SHIPMENT_START_DT = eventRow.cif.SHIPMENT_START_DT;
                    self.drWB_Override.cif.OVERRIDE_TYPE = type;

                    await self.SetOriginalEventDates();
                    self.dbContext.add(esWB_PEA_Event_Override, self.drWB_Override);

                    await self.syncSpends(eventRow.WB_EventSpend_ProductGroupRows);
                    if (self.drWB_Override.cif.OVERRIDE_TYPE == "Product") {
                        await self.syncProducts(eventRow.WB_Event_ProductGroupRows);
                    }

                    self.Identity.setAll(
                        self.WB_Override.cif.WB_OVERRIDE_ID,
                        self.WB_Override.WB_EventRow.cif.EVENT_ID,
                        self.WB_Override.WB_EventRow.cif.EVENT_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                } catch (err) {
                    self.LoadProgressProvider.exception(err);
                    error(err);
                }
            })();
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        const self = this;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");
        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                let sError = "Event Override already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null) self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Event Override.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = PEAEventOverrideBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = PEAEventOverrideBO.setupRecordResolver(recordResolver, [anId]);
            }
            recordResolverLoadPromise.then(function () {
                (async () => {
                    try {
                        let anOverrideRow: any = recordResolver.get(esWB_PEA_Event_Override, anId);
                        if (_.isEmpty(anOverrideRow)) {
                            self._OnLoadComplete = null;
                            self.LoadProgressProvider.exception("Id not found.");
                            resolve(self);
                            return;
                        }

                        self.drWB_Override = anOverrideRow;
                        let eventRow: WB_EventRow = anOverrideRow.WB_EventRow;
                        await self.SetOriginalEventDates();

                        await self.syncSpends(eventRow.WB_EventSpend_ProductGroupRows);
                        if (self.drWB_Override.cif.OVERRIDE_TYPE == "Product") {
                            await self.syncProducts(eventRow.WB_Event_ProductGroupRows);
                        }

                        self.Identity.setAll(
                            self.WB_Override.cif.WB_OVERRIDE_ID,
                            self.WB_Override.WB_EventRow.cif.EVENT_ID,
                            self.WB_Override.WB_EventRow.cif.EVENT_NAME,
                        );
                        self.LoadProgressProvider.setName(
                            "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                        );

                        self._bIsLoaded = true;
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.finish();
                        resolve(self);
                    } catch (err) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception(err);
                        error(err);
                    }
                })();
            });
        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async SetOriginalEventDates(): Promise<void> {
        let self = this;
        if (_.isEmpty(self.drWB_Override.WB_EventRow)) return;
        self.drWB_Override.InStoreDateRange =
            DateUtils.convertFromSAPBWToReadable(self.drWB_Override.WB_EventRow.cif.INSTORE_START_DT, "us") +
            " - " +
            DateUtils.convertFromSAPBWToReadable(self.drWB_Override.WB_EventRow.cif.INSTORE_END_DT, "us");
        self.drWB_Override.ShipmentDateRange =
            DateUtils.convertFromSAPBWToReadable(self.drWB_Override.WB_EventRow.cif.INSTORE_START_DT, "us") +
            " - " +
            DateUtils.convertFromSAPBWToReadable(self.drWB_Override.WB_EventRow.cif.INSTORE_END_DT, "us");

        self.appliedOverride = _.first(
            _.orderBy(
                await self.dbContext.findAll(
                    esWB_PEA_Event_Override,
                    {
                        where: {
                            WB_EVT_ID: self.WB_Override.cif.WB_EVT_ID,
                            STATUS: { inq: ["Superseded", "Applied"] },
                            OVERRIDE_TYPE: self.WB_Override.cif.OVERRIDE_TYPE,
                            DELETED_IND: { neq: "1" },
                        },
                    },
                    { with: [esWB_PEA_Event_ProductGroup_Override] },
                ),
                "MODIFIED_DTTS",
                "desc",
            ),
        );
    }

    async loadEvent(wbEventId: number, sType: string): Promise<WB_EventRow> {
        let self = this;

        let eventRecordResolver: ESRecordResolver = new ESRecordResolver(self.dbContext, {
            standardConditions: {
                [esWB_Event]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Event_Product_Group]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Event_Spend]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Event_Spend_PrdGrp]: { DELETED_IND: { notIn: ["1"] } },
            },
            dctExcludeHasManyNamedRelations: {
                [esWB_Event]: [
                    esWB_Event_Spend_PrdGrp_Redistrib,
                    esWB_Event_Customer,
                    esWB_Event_Meta,
                    esWB_Event_Tag,
                    esWB_Event_Relationship,
                    esWB_Event_Circular,
                    esWB_EventFR,
                    esWB_EvtShipPrd,
                    esWB_EvtSShpPrd,
                ],
                [esWB_Event_Spend_Type]: [esWB_Event_Spend_Type_Account],
                [esWB_Event_Spend_PrdGrp]: [esWB_Event_Spend_PrdGrp_Customer, esWB_Event_Spend_Prd],
            },
        });

        await eventRecordResolver.findAllWithRelationships(esWB_Event, [wbEventId]);
        let eventRow: WB_EventRow = <any>eventRecordResolver.get(esWB_Event, wbEventId);

        return eventRow;
    }

    get bEventDatesAligned(): boolean {
        let self = this;
        let eventRow = self.WB_Override.WB_EventRow.cif;
        let unalignedRows = _.filter(
            self.WB_Override.WB_EventRow.WB_EventSpend_ProductGroupRows,
            (row) =>
                row.isActive &&
                (row.cif.INSTORE_START_DT != eventRow.INSTORE_START_DT ||
                    row.cif.INSTORE_END_DT != eventRow.INSTORE_END_DT ||
                    row.cif.SHIPMENT_START_DT != eventRow.SHIPMENT_START_DT ||
                    row.cif.SHIPMENT_END_DT != eventRow.SHIPMENT_END_DT),
        );

        if (!_.isEmpty(unalignedRows)) return false;
        return true;
    }

    get bEventPPGsAligned(): boolean {
        let self = this;
        let bDiff = false;
        let ppgIds = _.map(
            _.filter(self.WB_Override.WB_EventRow.WB_Event_ProductGroupRows, (row) => row.isActive),
            "WB_PRDGRP_ID",
        );
        let tacticRows = _.groupBy(
            _.filter(self.WB_Override.WB_EventRow.WB_EventSpend_ProductGroupRows, (row) => row.isActive),
            "WB_EVTS_ID",
        );
        for (let key of _.keys(tacticRows)) {
            let idsInTactic = _.map(tacticRows[key], "WB_PRDGRP_ID");
            if (_.difference(ppgIds, idsInTactic).length) {
                bDiff = false;
                return;
            }
        }

        if (bDiff) return true;
        return true;
    }

    async syncSpends(spendPPGRows: WB_EventSpendPrdGrpRow[]): Promise<void> {
        let self = this;
        let main = self.drWB_Override.cif;
        let metaRows: WB_MetaRow[] = <WB_MetaRow[]>await self.dbContext.findAll(esWB_Meta, {
            with: {
                WB_META_ID: { inq: _.map(spendPPGRows, "PAYMENT_METHOD_ID") },
                META_TYPE_ID: "Payment Method",
            },
        });

        let spendRows: WB_EventSpendRow[] = _.filter(
            self.drWB_Override.WB_EventRow.WB_EventSpendRows,
            (row) => row.isActive,
        );
        let addRows: WB_EventSpendPrdGrpRow[] = [];
        let addSpendRows: WB_EventSpendRow[] = [];
        if (self.drWB_Override.isActiveNew) {
            addRows = spendPPGRows;
            addSpendRows = spendRows;
        } else {
            //tactics
            _.forEach(self.drWB_Override.WB_PEAEventSpendOverrideRows, (row) => {
                if (
                    row.isActive &&
                    !_.filter(spendRows, (spend) => spend.cif.WB_EVTS_ID == row.cif.WB_EVTS_ID).length
                ) {
                    row.markDeleted();

                    _.forEach(row.WB_PEAEventSpendProductGroupOverrideRows, (ppg) => {
                        if (ppg.isActive) row.markDeleted();
                    });
                }
            });
            let dctPPGsByTactic = _.groupBy(spendPPGRows, "WB_EVTS_ID");
            _.forEach(spendRows, (row) => {
                if (!row.isActive) return;
                let existingTactics = _.filter(
                    self.drWB_Override.WB_PEAEventSpendOverrideRows,
                    (spend) => spend.isActive && spend.cif.WB_EVTS_ID == row.cif.WB_EVTS_ID,
                );

                if (!existingTactics.length) {
                    addSpendRows.push(row);
                    addRows = _.concat(
                        addRows,
                        _.filter(dctPPGsByTactic[row.cif.WB_EVTS_ID], (ppg) => ppg.isActive),
                    );
                } else {
                    if (self.drWB_Override.cif.OVERRIDE_TYPE == "Date")
                        _.forEach(dctPPGsByTactic[row.cif.WB_EVTS_ID], (ppg) => {
                            addRows.push(ppg);
                        });
                }
            });
        }
        let tacticIds: number[] = <number[]>(
            await self.dbContext.getNewIds(esWB_PEA_Event_Spend_Override, addSpendRows.length)
        );
        let overrideRows: WB_PEAEventSpendOverrideRow[] = [];
        let ppgIds: number[] = <number[]>(
            await self.dbContext.getNewIds(esWB_PEA_Event_Spend_ProductGroup_Override, addRows.length)
        );
        let overridePPGRows: WB_PEAEventSpendProductGroupOverrideRow[] = [];
        let m = 0;
        let eventRow = self.drWB_Override.WB_EventRow.cif;
        _.forEach(addSpendRows, (row: WB_EventSpendRow, idx: number) => {
            let newSpendOverride: WB_PEAEventSpendOverrideRow = <WB_PEAEventSpendOverrideRow>(
                self.dbContext.createRecord(esWB_PEA_Event_Spend_Override)
            );
            newSpendOverride.cif.WB_OVERRIDE_EVTS_ID = tacticIds[idx];
            newSpendOverride.cif.WB_OVERRIDE_ID = main.WB_OVERRIDE_ID;
            newSpendOverride.WB_PEAEventOverrideRow = self.drWB_Override;
            newSpendOverride.cif.WB_EVTS_ID = row.cif.WB_EVTS_ID;
            newSpendOverride.WB_EventSpendRow = row;
            newSpendOverride.cif.STATUS = "Pending";

            _.forEach(
                _.filter(addRows, (ppg) => ppg.cif.WB_EVTS_ID == row.cif.WB_EVTS_ID && ppg.isActive),
                (ppg) => {
                    let newPPG: WB_PEAEventSpendProductGroupOverrideRow = <WB_PEAEventSpendProductGroupOverrideRow>(
                        self.dbContext.createRecord(esWB_PEA_Event_Spend_ProductGroup_Override)
                    );
                    newPPG.cif.WB_OVERRIDE_EVTSPG_ID = ppgIds[m];
                    newPPG.cif.WB_OVERRIDE_EVTS_ID = tacticIds[idx];
                    newPPG.WB_PEAEventSpendOverrideRow = newSpendOverride;
                    newPPG.cif.WB_PRDGRP_ID = ppg.cif.WB_PRDGRP_ID;
                    newPPG.WB_ProductGroupRow = ppg.WB_ProductGroupRow;
                    newPPG.cif.OVRD_INSTORE_START_DT = ppg.cif.INSTORE_START_DT;
                    newPPG.cif.OVRD_INSTORE_END_DT = ppg.cif.INSTORE_END_DT;
                    newPPG.cif.OVRD_SHIPMENT_START_DT = ppg.cif.SHIPMENT_START_DT;
                    newPPG.cif.OVRD_SHIPMENT_END_DT = ppg.cif.SHIPMENT_END_DT;
                    newPPG.cif.STATUS = "Pending";
                    overridePPGRows.push(newPPG);
                    m++;
                },
            );

            overrideRows.push(newSpendOverride);
        });
        if (!_.isEmpty(overrideRows)) self.dbContext.add(esWB_PEA_Event_Spend_Override, overrideRows);
        if (!_.isEmpty(overridePPGRows))
            self.dbContext.add(esWB_PEA_Event_Spend_ProductGroup_Override, overridePPGRows);

        _.forEach(self.WB_Override.WB_PEAEventSpendOverrideRows, (row) => {
            if (!row.isActive) return;
            row.cif.STATUS = main.STATUS;
            row.PAYMENT_METHOD =
                _.first(
                    _.filter(
                        metaRows,
                        (meta) => Number(row.WB_EventSpendRow.cif.PAYMENT_METHOD_ID) == meta.cif.WB_META_ID,
                    ),
                )?.cif?.META_NAME || "";

            row.INSTORE_START_DT = _.min(
                _.map(
                    _.filter(eventRow.WB_EventSpend_ProductGroupRows, (row) => row.isActive),
                    "INSTORE_START_DT",
                ),
            );
            row.INSTORE_END_DT = _.max(
                _.map(
                    _.filter(eventRow.WB_EventSpend_ProductGroupRows, (row) => row.isActive),
                    "INSTORE_END_DT",
                ),
            );
            row.SHIPMENT_START_DT = _.min(
                _.map(
                    _.filter(eventRow.WB_EventSpend_ProductGroupRows, (row) => row.isActive),
                    "SHIPMENT_START_DT",
                ),
            );
            row.SHIPMENT_END_DT = _.max(
                _.map(
                    _.filter(eventRow.WB_EventSpend_ProductGroupRows, (row) => row.isActive),
                    "SHIPMENT_END_DT",
                ),
            );
            if (row.isActiveNew) {
                row.OVRD_INSTORE_START_DT = row.INSTORE_START_DT;
                row.OVRD_INSTORE_END_DT = row.INSTORE_END_DT;
                row.OVRD_SHIPMENT_START_DT = row.SHIPMENT_START_DT;
                row.OVRD_SHIPMENT_END_DT = row.SHIPMENT_END_DT;
            } else {
                let ppgRows = _.filter(row.WB_PEAEventSpendProductGroupOverrideRows, (p) => p.isActive);
                row.OVRD_INSTORE_START_DT = _.min(_.map(ppgRows, "OVRD_INSTORE_START_DT"));
                row.OVRD_INSTORE_END_DT = _.max(_.map(ppgRows, "OVRD_INSTORE_END_DT"));
                row.OVRD_SHIPMENT_START_DT = _.min(_.map(ppgRows, "OVRD_SHIPMENT_START_DT"));
                row.OVRD_SHIPMENT_END_DT = _.max(_.map(ppgRows, "OVRD_SHIPMENT_END_DT"));
            }

            _.forEach(row.WB_PEAEventSpendProductGroupOverrideRows, (ppg) => {
                if (!ppg.isActive) return;
                let tacticPPG = _.first(
                    _.filter(
                        eventRow.WB_EventSpend_ProductGroupRows,
                        (t) => t.cif.WB_EVTS_ID == row.cif.WB_EVTS_ID && t.cif.WB_PRDGRP_ID == ppg.cif.WB_PRDGRP_ID,
                    ),
                );
                if (ppg.isActiveNew) {
                    ppg.INSTORE_START_DT = tacticPPG.cif.INSTORE_START_DT;
                    ppg.INSTORE_END_DT = tacticPPG.cif.INSTORE_END_DT;
                    ppg.SHIPMENT_START_DT = tacticPPG.cif.SHIPMENT_START_DT;
                    ppg.SHIPMENT_END_DT = tacticPPG.cif.SHIPMENT_END_DT;
                    ppg.cif.OVRD_INSTORE_START_DT = tacticPPG.cif.INSTORE_START_DT;
                    ppg.cif.OVRD_INSTORE_END_DT = tacticPPG.cif.INSTORE_END_DT;
                    ppg.cif.OVRD_SHIPMENT_START_DT = tacticPPG.cif.SHIPMENT_START_DT;
                    ppg.cif.OVRD_SHIPMENT_END_DT = tacticPPG.cif.SHIPMENT_END_DT;
                }
            });
        });
        return;
    }

    async syncProducts(ppgRows: WB_EventProductGroupRow[]): Promise<void> {
        let self = this;
        let main = self.drWB_Override.cif;

        let planPGs = await self.dbContext.findAll(
            esWB_Plan_Product_Group,
            {
                where: {
                    DELETED_IND: { neq: "1" },
                    WB_PLAN_ID: self.drWB_Override.WB_EventRow.WB_PlanRow.cif.WB_PLAN_ID,
                },
            },
            { with: [esWB_Product_Group] },
        );
        self.planPPGs = _.flatMap(planPGs, (pg) => {
            return pg.WB_ProductGroupRow;
        });

        let addRows: WB_EventProductGroupRow[] = [];
        if (self.drWB_Override.isActiveNew) addRows = ppgRows;
        else {
            _.forEach(ppgRows, (row) => {
                if (
                    row.isActive &&
                    !_.filter(
                        self.drWB_Override.WB_PEAEventProductGroupOverrideRows,
                        (spend) => spend.isActive && spend.cif.WB_PRDGRP_ID == row.cif.WB_PRDGRP_ID,
                    ).length
                )
                    addRows.push(row);
            });
        }
        let Ids: number[] = <number[]>(
            await self.dbContext.getNewIds(esWB_PEA_Event_ProductGroup_Override, addRows.length)
        );

        let overrideRows: WB_PEAEventProductGroupOverrideRow[] = [];

        _.forEach(addRows, (row: WB_EventProductGroupRow, idx: number) => {
            let newPPGOverride: WB_PEAEventProductGroupOverrideRow = <WB_PEAEventProductGroupOverrideRow>(
                self.dbContext.createRecord(esWB_PEA_Event_ProductGroup_Override)
            );
            newPPGOverride.cif.WB_OVERRIDE_PRDGRP_ID = Ids[idx];
            newPPGOverride.cif.WB_OVERRIDE_ID = main.WB_OVERRIDE_ID;
            newPPGOverride.WB_PEAEventOverrideRow = self.drWB_Override;
            newPPGOverride.cif.WB_PRDGRP_ID = row.cif.WB_PRDGRP_ID;
            newPPGOverride.WB_ProductGroupRow = row.WB_ProductGroupRow;
            newPPGOverride.cif.STATUS = "Pending";
            overrideRows.push(newPPGOverride);
        });
        if (!_.isEmpty(overrideRows)) self.dbContext.add(esWB_PEA_Event_ProductGroup_Override, overrideRows);

        _.forEach(self.drWB_Override.WB_PEAEventProductGroupOverrideRows, (row) => {
            let applied = _.filter(
                self.appliedOverride?.WB_PEAEventProductGroupOverrideRows,
                (p) => p.isActive && p.cif.WB_PRDGRP_ID == row.cif.WB_PRDGRP_ID,
            );
            if (!_.isEmpty(applied)) {
                if (_.first(applied).cif.ACTION == "Add")
                    row.CURRENT_OVERRIDE = "Applied (" + _.first(applied).cif.ACTION + " to event)";
                else row.CURRENT_OVERRIDE = "Applied (" + _.first(applied).cif.ACTION + " from event)";
            } else {
                row.CURRENT_OVERRIDE = "None";
            }
            if (!_.isEmpty(row.cif.ACTION))
                if (row.cif.ACTION == "Add")
                    row.REQUESTED_OVERRIDE = row.cif.STATUS + " (" + row.cif.ACTION + " to event)";
                else row.REQUESTED_OVERRIDE = row.cif.STATUS + " (" + row.cif.ACTION + " from event)";
            else row.REQUESTED_OVERRIDE = "None";
        });

        let existingTactic = _.groupBy(
            _.filter(self.drWB_Override.WB_EventRow.WB_EventSpend_ProductGroupRows, (row) => row.isActive),
            "WB_EVTS_ID",
        );
        _.forEach(self.drWB_Override.WB_PEAEventSpendOverrideRows, async (tactic) => {
            if (!tactic.isActive) return;
            let existingPPG = _.groupBy(existingTactic[tactic.cif.WB_EVTS_ID], "WB_PRDGRP_ID");

            let ppgTactic = _.groupBy(tactic.WB_PEAEventSpendProductGroupOverrideRows, "WB_PRDGRP_ID");
            let addPPGs: any[] = [];
            _.forEach(self.drWB_Override.WB_PEAEventProductGroupOverrideRows, (ppg) => {
                if (!ppg.isActive) return;
                if (_.isEmpty(ppgTactic[ppg.cif.WB_PRDGRP_ID])) {
                    addPPGs.push(ppg);
                } else {
                    let ppgt = _.first(ppgTactic[ppg.cif.WB_PRDGRP_ID]);
                    if (ppgt.isActive) _.first(ppgTactic[ppg.cif.WB_PRDGRP_ID]).selected = true;
                    else {
                        if (_.isEmpty(existingPPG[ppg.cif.WB_PRDGRP_ID])) {
                            ppgt.cif.DELETED_IND = "0";
                            ppgt.cif.ACTION = tactic.cif.ACTION;
                            ppgt.cif.STATUS = tactic.cif.STATUS;
                        }
                    }
                }
            });

            let ppgIds = <number[]>await self.dbContext.getNewIds(esWB_PEA_Event_ProductGroup_Override, addPPGs.length);
            _.forEach(addPPGs, (row: WB_ProductGroupRow, idx: number) => {
                let newPPGRow = <WB_PEAEventSpendProductGroupOverrideRow>(
                    self.dbContext.createRecord(esWB_PEA_Event_Spend_ProductGroup_Override)
                );
                newPPGRow.cif.WB_OVERRIDE_EVTSPG_ID = ppgIds[idx];
                newPPGRow.cif.WB_PRDGRP_ID = row.cif.WB_PRDGRP_ID;
                newPPGRow.WB_ProductGroupRow = row;
                newPPGRow.cif.WB_OVERRIDE_EVTS_ID = tactic.cif.WB_OVERRIDE_EVTS_ID;
                newPPGRow.WB_PEAEventSpendOverrideRow = tactic;
                newPPGRow.cif.STATUS = tactic.cif.STATUS;
                newPPGRow.cif.ACTION = tactic.cif.ACTION;
                newPPGRow.selected = false;
                self.dbContext.add(esWB_PEA_Event_Spend_ProductGroup_Override, newPPGRow);
            });
        });
        return;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            "Validating " +
                self.WB_Override.WB_EventRow.cif.EVENT_NAME +
                "(" +
                self.WB_Override.WB_EventRow.cif.EVENT_ID +
                ")",
        );
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate Event Override.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            self._bIsValidated = true;
            self.ValidateProgressProvider.finish();
            resolve(self);
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        this.SaveProgressProvider.start(
            "Saving " +
                self.WB_Override.WB_EventRow.cif.EVENT_NAME +
                "(" +
                self.WB_Override.WB_EventRow.cif.EVENT_ID +
                ")",
        );

        return new Promise<IBusinessObject>((resolve, reject) => {
            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Event Override.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.SaveOptions = options || {};

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any,
            ) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            ASync.resolve()
                .then(() => {
                    const tasks: Promise<any>[] = [];
                    if (self.drWB_Override.cif.STATUS == "New") self.drWB_Override.cif.STATUS = "Pending";

                    if (_.filter(self.drWB_Override.WB_PEAEventSpendOverrideRows, (row) => row.isActive).length) {
                        let dctTactic = _.groupBy(
                            _.filter(
                                self.drWB_Override.WB_EventRow.WB_EventSpend_ProductGroupRows,
                                (row) => row.isActive,
                            ),
                            "WB_EVTS_ID",
                        );
                        _.forEach(self.drWB_Override.WB_PEAEventSpendOverrideRows, (tactic) => {
                            if (!tactic.isActive) return;
                            let dctPPG = _.groupBy(dctTactic[tactic.cif.WB_EVTS_ID], "WB_PRDGRP_ID");
                            _.forEach(tactic.WB_PEAEventSpendProductGroupOverrideRows, (spend) => {
                                if (!spend.isActive) return;

                                let bExist = false;

                                let ppgRow = _.first(dctPPG[spend.cif.WB_PRDGRP_ID]);

                                if (
                                    self.drWB_Override.cif.OVERRIDE_TYPE == "Product" &&
                                    !_.isEmpty(ppgRow) &&
                                    spend.selected
                                )
                                    bExist = true;
                                else if (
                                    self.drWB_Override.cif.OVERRIDE_TYPE == "Date" &&
                                    !_.isEmpty(ppgRow) &&
                                    spend.cif.ACTION !== "Clear" &&
                                    ppgRow.cif.INSTORE_START_DT == spend.cif.OVRD_INSTORE_START_DT &&
                                    ppgRow.cif.INSTORE_END_DT == spend.cif.OVRD_INSTORE_END_DT &&
                                    ppgRow.cif.SHIPMENT_START_DT == spend.cif.OVRD_SHIPMENT_START_DT &&
                                    ppgRow.cif.SHIPMENT_END_DT == spend.cif.OVRD_SHIPMENT_END_DT
                                )
                                    bExist = true;

                                if (bExist) spend.markDeleted();
                            });

                            if (
                                !_.filter(tactic.WB_PEAEventSpendProductGroupOverrideRows, (row) => row.isActive).length
                            )
                                tactic.markDeleted();
                        });
                    }
                    if (self.drWB_Override.cif.OVERRIDE_TYPE == "Product") {
                        _.forEach(self.drWB_Override.WB_PEAEventProductGroupOverrideRows, (row) => {
                            if (row.isActive && row.REQUESTED_OVERRIDE == "None" && row.CURRENT_OVERRIDE == "None")
                                row.markDeleted();
                        });

                        if (
                            !_.filter(self.drWB_Override.WB_PEAEventProductGroupOverrideRows, (row) => row.isActive)
                                .length
                        ) {
                            self.drWB_Override.markDeleted();
                            _.forEach(self.drWB_Override.WB_PEAEventSpendOverrideRows, (tactic) => {
                                _.forEach(tactic.WB_PEAEventSpendProductGroupOverrideRows, (spend) => {
                                    spend.markDeleted();
                                });
                                tactic.markDeleted();
                            });
                        }
                    }

                    if (
                        self.drWB_Override.cif.OVERRIDE_TYPE == "Date" &&
                        !_.filter(
                            self.drWB_Override.WB_PEAEventSpendOverrideRows,
                            (row) => row.isActive && row.cif.ACTION == "Clear",
                        ).length &&
                        self.drWB_Override.WB_EventRow.cif.INSTORE_START_DT ==
                            self.drWB_Override.cif.OVRD_INSTORE_START_DT &&
                        self.drWB_Override.WB_EventRow.cif.INSTORE_END_DT ==
                            self.drWB_Override.cif.OVRD_INSTORE_END_DT &&
                        self.drWB_Override.WB_EventRow.cif.SHIPMENT_START_DT ==
                            self.drWB_Override.cif.OVRD_SHIPMENT_START_DT &&
                        self.drWB_Override.WB_EventRow.cif.SHIPMENT_END_DT ==
                            self.drWB_Override.cif.OVRD_SHIPMENT_END_DT
                    ) {
                        if (!_.filter(self.drWB_Override.WB_PEAEventSpendOverrideRows, (row) => row.isActive).length) {
                            //only when tactics are not changed, then delete
                            self.drWB_Override.markDeleted();
                        }
                    }

                    tasks.push(saveFunction(esWB_PEA_Event_Override, self.drWB_Override, {}));
                    if (!_.isEmpty(self.appliedOverride)) {
                        if (self.appliedOverride.cif.STATUS == "Applied")
                            self.appliedOverride.cif.STATUS = "Superseded";
                        else if (
                            self.appliedOverride.cif.STATUS == "Superseded" &&
                            !self.drWB_Override.isActive &&
                            self.drWB_Override.cif.STATUS == "Pending"
                        )
                            self.appliedOverride.cif.STATUS = "Applied";

                        tasks.push(saveFunction(esWB_PEA_Event_Override, self.appliedOverride, {}));
                    }

                    tasks.push(
                        saveFunction(
                            esWB_PEA_Event_ProductGroup_Override,
                            self.drWB_Override.WB_PEAEventProductGroupOverrideRows,
                            {},
                        ),
                    );

                    tasks.push(
                        saveFunction(
                            esWB_PEA_Event_Spend_Override,
                            self.drWB_Override.WB_PEAEventSpendOverrideRows,
                            {},
                        ),
                    );

                    tasks.push(
                        saveFunction(
                            esWB_PEA_Event_Spend_ProductGroup_Override,
                            _.flatMap(self.drWB_Override.WB_PEAEventSpendOverrideRows, (row) => {
                                return row.WB_PEAEventSpendProductGroupOverrideRows;
                            }),
                        ),
                    );

                    return Promise.all(tasks);
                })
                .then((ignored) => {
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        });
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    resolve(self);
                });
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        let self = this;

        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " +
                self.drWB_Override.WB_EventRow.cif.EVENT_NAME +
                "(" +
                self.drWB_Override.WB_EventRow.cif.EVENT_ID +
                ")",
        );
        return new Promise<IBusinessObject>((resolve, error) => {
            // 1. Validate if we can delete the Customer

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Override.markDeleted();
            _.forEach(self.drWB_Override.WB_PEAEventSpendOverrideRows, (row) => {
                if (row.isActive) row.markDeleted();
            });

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const override: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {
                [esWB_Event]: [esWB_Event_Product_Group, esWB_Event_Spend],
                [esWB_Event_Spend]: [esWB_Event_Spend_PrdGrp],
            },
        };

        const aRecResolver = new ESRecordResolver(dbContext, override);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_PEA_Event_Override, ids)
                .then((result) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    async addProductGroups(ppgWBIds: number[]): Promise<PEAEventOverrideBO> {
        let self = this;

        let ppgIds: number[] = <number[]>(
            await self.dbContext.getNewIds(esWB_PEA_Event_ProductGroup_Override, ppgWBIds.length)
        );
        let ppgRows = <WB_ProductGroupRow[]>await self.dbContext.findUsingCache(esWB_Product_Group, ppgWBIds);
        let overridePPGRows: WB_PEAEventProductGroupOverrideRow[] = [];
        let overrideSPPGRows: WB_PEAEventSpendProductGroupOverrideRow[] = [];
        let m = 0;
        for (let ppg of ppgWBIds) {
            let newPPG: WB_PEAEventProductGroupOverrideRow = <WB_PEAEventProductGroupOverrideRow>(
                self.dbContext.createRecord(esWB_PEA_Event_ProductGroup_Override)
            );
            newPPG.cif.WB_OVERRIDE_PRDGRP_ID = ppgIds[m];
            newPPG.cif.WB_PRDGRP_ID = ppg;
            newPPG.WB_ProductGroupRow = _.first(_.filter(ppgRows, (row) => row.cif.WB_PRDGRP_ID == ppg));
            newPPG.cif.WB_OVERRIDE_ID = self.drWB_Override.cif.WB_OVERRIDE_ID;
            newPPG.WB_PEAEventOverrideRow = self.drWB_Override;
            newPPG.cif.ACTION = "Add";
            newPPG.cif.STATUS = "Pending";
            newPPG.REQUESTED_OVERRIDE = newPPG.cif.STATUS + " (" + newPPG.cif.ACTION + " to event)";

            for (let tactic of self.WB_Override.WB_PEAEventSpendOverrideRows) {
                let newSPG: WB_PEAEventSpendProductGroupOverrideRow = <WB_PEAEventSpendProductGroupOverrideRow>(
                    await self.dbContext.createRecordWithKey(esWB_PEA_Event_Spend_ProductGroup_Override)
                );

                newSPG.cif.WB_OVERRIDE_EVTSPG_ID = ppgIds[m];
                newSPG.cif.WB_OVERRIDE_EVTS_ID = tactic.cif.WB_OVERRIDE_EVTS_ID;
                newSPG.WB_PEAEventSpendOverrideRow = tactic;
                newSPG.cif.WB_PRDGRP_ID = ppg;
                newSPG.WB_ProductGroupRow = _.first(_.filter(ppgRows, (row) => row.cif.WB_PRDGRP_ID == ppg));
                newSPG.cif.OVRD_INSTORE_START_DT = self.drWB_Override.WB_EventRow.cif.INSTORE_START_DT;
                newSPG.cif.OVRD_INSTORE_END_DT = self.drWB_Override.WB_EventRow.cif.INSTORE_END_DT;
                newSPG.cif.OVRD_SHIPMENT_START_DT = self.drWB_Override.WB_EventRow.cif.SHIPMENT_START_DT;
                newSPG.cif.OVRD_SHIPMENT_END_DT = self.drWB_Override.WB_EventRow.cif.SHIPMENT_END_DT;
                newSPG.INSTORE_START_DT = self.drWB_Override.WB_EventRow.cif.INSTORE_START_DT;
                newSPG.INSTORE_END_DT = self.drWB_Override.WB_EventRow.cif.INSTORE_END_DT;
                newSPG.SHIPMENT_START_DT = self.drWB_Override.WB_EventRow.cif.SHIPMENT_START_DT;
                newSPG.SHIPMENT_END_DT = self.drWB_Override.WB_EventRow.cif.SHIPMENT_END_DT;
                newSPG.cif.ACTION = "Add";
                overrideSPPGRows.push(newSPG);
            }
            overridePPGRows.push(newPPG);
            m++;
        }
        if (overrideSPPGRows.length) self.dbContext.add(esWB_PEA_Event_Spend_ProductGroup_Override, overrideSPPGRows);
        if (overridePPGRows.length) self.dbContext.add(esWB_PEA_Event_ProductGroup_Override, overridePPGRows);
        return self;
    }

    async removeProductGroup(sppgWBId: number, sType: string): Promise<void> {
        let self = this;
        let row = _.first(
            _.filter(
                self.WB_Override.WB_PEAEventProductGroupOverrideRows,
                (row) => row.isActive && row.cif.WB_OVERRIDE_PRDGRP_ID == sppgWBId,
            ),
        );

        if (_.isEmpty(row)) return;
        row.REQUESTED_OVERRIDE = "Pending (" + sType + (sType == "Clear" ? " override to" : " from ") + "event)";

        if (sType == "Clear" && row.cif.STATUS == "Pending") {
            if (row.cif.ACTION == "Remove") {
                row.REQUESTED_OVERRIDE = "None";
                row.cif.ACTION = "";
            } else if (row.cif.ACTION == "Add") {
                row.markDeleted();
            }
        } else row.cif.ACTION = sType;

        _.forEach(self.drWB_Override.WB_PEAEventSpendOverrideRows, (tactic) => {
            if (!tactic.isActive) return;

            let sppgs = _.filter(
                tactic.WB_PEAEventSpendProductGroupOverrideRows,
                (ppg) => ppg.cif.WB_PRDGRP_ID == row.cif.WB_PRDGRP_ID,
            );

            if (sType == "Remove") {
                let sppg = _.first(_.filter(sppgs, (ppg) => ppg.isActive));
                sppg.cif.ACTION = sType;
                sppg.markDeleted();
            }

            if (sType == "Clear" && row.cif.STATUS == "Pending") {
                if (row.cif.ACTION == "") {
                    let sppg = _.first(sppgs);
                    sppg.cif.ACTION = "";
                    sppg.cif.DELETED_IND = "0";
                    sppg.selected = true;
                }

                if (row.cif.ACTION == "Add") {
                    let sppg = _.first(_.filter(sppgs, (ppg) => ppg.isActive));
                    sppg.markDeleted();
                }
            }
        });
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(PEAEventOverrideBO, "PEAEventOverrideBO", "PEA Event Override");
